import type { ReactElement } from "react";
import { Overlay } from "../ui/Overlay/Overlay";
import { Brand } from "./Brand";
import { NavItem } from "./NavItem";
import { Icon } from "../ui/Icon/Icon";
import { navigation } from "../lib/navigation";
import { avatarColor, avatarInitials } from "../lib/avatar";
import { useIdentity } from "../lib/user";
import styles from "./NavDrawer.module.css";

export interface NavDrawerProps {
  open: boolean;
  onClose: () => void;
  currentPath: string;
  onSignOut: () => void;
  onNavigate?: (href: string) => void;
}

export function NavDrawer({
  open,
  onClose,
  currentPath,
  onSignOut,
  onNavigate,
}: NavDrawerProps): ReactElement {
  const user = useIdentity();
  return (
    <Overlay
      open={open}
      onClose={onClose}
      placement="sheet-start"
      label="Navigation"
      panelStyle={{ inlineSize: "302px", padding: 0, overflow: "hidden" }}
    >
      <div className={styles.drawer}>
        <div className={styles.header}>
          <Brand size={26} className={styles.logo} />
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label="Close menu"
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        <nav className={styles.body} aria-label="Primary">
          {navigation.map((item) => (
            <NavItem
              key={item.key}
              item={item}
              active={currentPath.startsWith(item.href)}
              onNavigate={(href) => {
                onClose();
                onNavigate?.(href);
              }}
            />
          ))}
        </nav>

        <div className={styles.footer}>
          <span
            className={styles.avatar}
            style={{ background: avatarColor(user) }}
            aria-hidden="true"
          >
            {avatarInitials(user)}
          </span>
          <div className={styles.identity}>
            <span className={styles.name}>{user.name || user.email}</span>
            <span className={styles.org}>{user.org || user.email}</span>
          </div>
          <button type="button" className={styles.signout} onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </div>
    </Overlay>
  );
}
